"use client";
import { useEffect, useRef, useState } from "react";
import { StackList, MapStackToIcon, type Stack } from "@/components/Stack";
import { useTranslations } from "next-intl";
import { randInt } from "@/utils";
import useFramerMotion, {
  m,
  LazyMotion,
  type Variants,
} from "@/hooks/useFramerMotion";
import Heading from "@/components/ui/Heading";

const variants: Variants = {
  opened: {
    transition: {
      type: "spring",
      duration: 0.4,
      staggerChildren: 0.05,
      delayChildren: 0.2,
    },
  },
  closed: {
    transition: {
      type: "spring",
      duration: 0.4,
    },
  },
};
const iconVariants: Variants = {
  opened: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      duration: 0.4,
    },
  },
  closed: {
    scale: 1.25,
    opacity: 0,
    transition: {
      type: "spring",
      duration: 0.2,
    },
  },
};

const labelVariants: Variants = {
  opened: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      duration: 0.4,
    },
  },
  closed: {
    x: -10,
    opacity: 0,
    transition: {
      type: "spring",
      duration: 0.2,
    },
  },
};

const Skills = () => {
  const framerMotionFeatures = useFramerMotion();
  const t = useTranslations("about.skillsList");
  //bg-gradient-to-t from-accent/5 to-mint/5
  //      <div className="section-px skew-y-[1.5deg] bg-gradient-to-t from-accent/5 via-mint/5 to-dark/0 ~mt-16/20">
  return (
    <LazyMotion features={framerMotionFeatures}>
      <div className="section-px skew-y-3 bg-gradient-to-t from-accent/10 via-mint/5 to-dark/0 ~mt-16/20">
        <div className="grid skew-y-[-1.5deg] grid-cols-1 ~gap-5/10 ~pb-20/36 lg:grid-cols-4 lg:!gap-0 [&>div+div]:border-mint/10 lg:[&>div+div]:border-l-2">
          <SkillSet
            list="tools"
            label={t("tools")}
          />
          <SkillSet
            list="frontend"
            label={t("frontend")}
          />
          <SkillSet
            list="backend"
            label={t("backend")}
          />
          <SkillSet
            list="other"
            label={t("other")}
          />
        </div>
        {/* <div className="animate-moveMask pointer-events-none absolute bottom-0 left-0 -z-50 h-24 w-full bg-dark [mask-image:url('/assets/bg-divider-inv.webp')] [mask-position:240px_100%] [mask-repeat:repeat-x] [mask-size:960px_100%]" /> */}
        <div
          className={`absolute bottom-0 left-0 -z-10 h-32 w-full bg-transparent bg-[length:7px_7px] [background-image:radial-gradient(rgba(0,_0,_0,_0)_1px,_var(--clr-primary-dark)_1px)] [mask:linear-gradient(to_top,_rgb(0,_0,_0)_0%,_rgba(0,_0,_0,_0)_100%)]`}
        />
        <div className="absolute inset-x-0 top-full h-[2px] bg-accent/10" />

        {/* <div className="animate-moveMask pointer-events-none absolute left-0 top-0 -z-50 h-24 w-full -scale-100 bg-dark [mask-image:url('/assets/bg-divider-inv.webp')] [mask-position:240px_100%] [mask-repeat:repeat-x] [mask-size:960px_100%]" />
         */}

        {/* <div className="absolute inset-x-0 top-full h-[3px] bg-accent/10" /> */}
        {/* <div className="absolute bottom-[calc(100%-0.5rem)] left-0 -z-10 h-32 w-full bg-dark [mask-image:url('/assets/bg-divider-inv.webp')] [mask-position:240px_0px] [mask-repeat:repeat-x] [mask-size:960px_100%]" /> */}
      </div>
    </LazyMotion>
  );
};

Skills.displayName = "Skills";

const SkillSet = ({ list, label }: { list: Stack; label: string }) => {
  const itemsFiltered = StackList[list].filter((item) => item !== "Wordpress");
  const [items, setItems] = useState(itemsFiltered);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const randomize = () => {
    const randomizedItems = [...itemsFiltered].sort(() => Math.random() - 0.5);
    setItems(() => randomizedItems);
  };

  const setupTimeout = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    const random = randInt(12000, 22000);
    timeout.current = setTimeout(() => {
      randomize();
      setupTimeout();
    }, random);
  };
  useEffect(() => {
    randomize();
    setupTimeout();
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative z-30 text-center ~gap-8/20 first:pt-0 max-lg:~pt-8/10 lg:~px-4/5">
      <Heading
        level={5}
        className="rounded-lg bg-mint/10 py-4 text-mint md:tracking-wider">
        {label}
      </Heading>
      <m.ul
        layoutRoot
        className="mt-8 flex flex-wrap-reverse justify-center gap-x-4 gap-y-6 md:tracking-wider">
        {items.map((item, index) => {
          const Icon = MapStackToIcon(item);
          return (
            <m.li
              variants={{
                ...variants,
                ...{
                  opened: {
                    transition: {
                      delayChildren: index * 0.05,
                    },
                  },
                },
              }}
              layout
              layoutId={`${list}-${item}`}
              whileInView={"opened"}
              viewport={{ amount: "some" }}
              initial="closed"
              exit={"closed"}
              key={item}
              className="w-[23%] xs:w-1/5 md:w-[10%] lg:w-1/3 xl:w-[28%]">
              <div className="relative text-center ~pb-6/8">
                <m.div
                  variants={iconVariants}
                  className="mx-auto flex aspect-square max-w-min skew-y-[-1.5deg] items-center justify-center rounded-full border border-white/15 bg-dark/50 p-2 text-primary/70 lg:p-3 xl:p-4 [&_svg]:~size-6/9">
                  {Icon}
                </m.div>

                <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
                  <m.h4
                    variants={labelVariants}
                    className="hyphens-auto whitespace-nowrap font-secondary text-primary/60 ~text-xs/[0.8125rem]">
                    {item}
                  </m.h4>
                </div>
              </div>
            </m.li>
          );
        })}
      </m.ul>
    </div>
  );
};

export default Skills;
